import React, { useState, useEffect, useRef, useCallback } from 'react';
import Select from 'react-select';
import InfiniteScroll from 'react-infinite-scroller';
import { WidgetHelper } from '../WidgetHelper';
import { ISearchResultItem, IDropDownValue } from '../../Models';
import { ContentLink } from '../../Components';
import { ContentService } from '../../Services';

interface IContentSearchWidgetProps {
    currentAreaId: number;
    searchOfferUrl: string;
    searchString: string;
    dictResultForLabel: string;
    dictResultLabel: string;
    dictSearchLabel: string;
    dictSearchOfferLabel: string;
    dictAreasLabel: string;
    dictNoResultTitle: string;
    dictNoResultDescription: string;
    dictNoResultForCurrentAreaTitle: string;
    dictResultsLabel: string;
}

export const ContentSearchWidget: React.FC<IContentSearchWidgetProps> = (
    props
) => {
    const [currentAreaHasResult, setCurrentAreaHasResult] = useState(false);
    const [hasMoreResult, setHasMoreResult] = useState(false);
    const [, setPageCurrent] = useState(0);
    const [searchResultCount, setSearchResultCount] = useState(0);
    const [searchResultItems, setSearchResultItems] = useState<
        ISearchResultItem[]
    >([]);
    const [searchString, setSearchString] = useState(props.searchString);
    const [searchStringCurrent, setSearchStringCurrent] = useState(
        props.searchString
    );
    const [areas, setAreas] = useState<IDropDownValue[]>([]);
    const [areaSelected, setAreaSelected] = useState<
        IDropDownValue | undefined
    >(undefined);
    const [isLoading, setIsLoading] = useState(true);
    const [hasSearched, setHasSearched] = useState(false);

    const cancellationTokenRef = useRef<AbortController | null>(null);

    const getAllSpaces = useCallback(async () => {
        try {
            const areas = await ContentService.getAllArea();
            setAreas(
                areas
                    .sort((a, b) => a.sortOrder - b.sortOrder)
                    .map((t) => ({
                        value: t.id.toString(),
                        label: t.name,
                    }))
            );
        } catch (e) {
            if (!(e instanceof DOMException) || e.name !== 'AbortError') {
                throw e;
            }
        }
    }, []);

    const getSearchResult = useCallback(
        async (query?: string, page?: number) => {
            if (cancellationTokenRef.current) {
                cancellationTokenRef.current.abort();
            }
            cancellationTokenRef.current = new AbortController();

            const effectiveQuery = query ?? searchStringCurrent;
            const areaId = areaSelected ? Number(areaSelected.value) : null;

            try {
                const result = await ContentService.search(
                    effectiveQuery,
                    props.currentAreaId,
                    areaId,
                    page ?? 0,
                    cancellationTokenRef.current.signal
                );

                const items = result.items ?? [];

                if (items.length > 0) {
                    setCurrentAreaHasResult(result.currentAreaHasResult);
                    setPageCurrent(result.pageNumber + 1);
                    setSearchResultCount(result.totalItems);
                    setSearchResultItems((prev) => {
                        const updated = prev.concat(items);
                        setHasMoreResult(updated.length < result.totalItems);
                        return updated;
                    });
                } else {
                    setCurrentAreaHasResult(false);
                    setHasMoreResult(false);
                    setPageCurrent(0);
                    setSearchResultCount(0);
                    setSearchResultItems([]);
                }

                setIsLoading(false);
            } catch (e) {
                if (!(e instanceof DOMException) || e.name !== 'AbortError') {
                    throw e;
                }
            }
        },
        [areaSelected, searchStringCurrent, props.currentAreaId]
    );

    useEffect(() => {
        const initialize = async () => {
            await getAllSpaces();
            if (searchString && searchString.trim().length > 0) {
                await getSearchResult();
            } else {
                setIsLoading(false);
            }
        };

        void initialize();
    }, [getAllSpaces, getSearchResult, searchString]);

    const handleChangeSearchString = (
        event: React.ChangeEvent<HTMLInputElement>
    ) => {
        setSearchString(event.target.value);
    };

    const handleChangeSpace = (area: IDropDownValue) => {
        setAreaSelected(area);
    };

    const handleLoadMore = () => {
        void getSearchResult();
    };

    const handleSubmit = (event: React.FormEvent) => {
        event.preventDefault();
        if (searchString == '') return;
        window.history.replaceState(
            null,
            '',
            `?search=${encodeURIComponent(searchString)}`
        );

        setCurrentAreaHasResult(false);
        setIsLoading(true);
        setHasMoreResult(false);
        setPageCurrent(0);
        setSearchResultCount(0);
        setSearchResultItems([]);
        setSearchStringCurrent(searchString);
        setHasSearched(true);

        void getSearchResult(searchString, 0);
    };

    return (
        <>
            <div className="banner banner--colored banner--colored__global-search">
                <div className="container">
                    <div className="row">
                        <div className="col-12 col-lg-8 offset-lg-2">
                            <h1>
                                {props.dictResultForLabel} {'"'}
                                {searchString}
                                {'"'}
                            </h1>
                            <form
                                className="job-search job-search--global"
                                onSubmit={handleSubmit}
                                data-skip-captcha="true"
                            >
                                <div className="form-group">
                                    <div className="search">
                                        <label className="sr-only sr-only-focusable">
                                            {props.dictSearchLabel}
                                        </label>
                                        <input
                                            title={props.searchString}
                                            type="text"
                                            name="search"
                                            value={searchString}
                                            placeholder={`${props.dictSearchLabel}...`}
                                            onChange={handleChangeSearchString}
                                        />
                                    </div>
                                </div>
                                <div className="form-group">
                                    <div className="field-icon objectif">
                                        <label
                                            htmlFor="areas-select"
                                            className="sr-only"
                                        >
                                            {props.dictAreasLabel}
                                        </label>

                                        <Select
                                            inputId="areas-select"
                                            className="domain-select-container"
                                            classNamePrefix="domain-select"
                                            isClearable={true}
                                            isSearchable={false}
                                            options={areas}
                                            onChange={handleChangeSpace}
                                            placeholder={props.dictAreasLabel}
                                            value={areaSelected}
                                        />
                                    </div>
                                </div>
                                <div className="form-group submit">
                                    <button
                                        type="submit"
                                        className="gtm-global-search"
                                    >
                                        <i className="icon-search"></i>
                                        {props.dictSearchLabel}
                                    </button>
                                </div>
                            </form>
                            {props.searchOfferUrl.length > 0 && (
                                <div className="align-right">
                                    <a
                                        href={props.searchOfferUrl}
                                        className="more more--small"
                                    >
                                        {props.dictSearchOfferLabel}
                                    </a>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
            <div className="container">
                <div className="row">
                    <div className="col-12 col-lg-8 offset-lg-2">
                        {isLoading && (
                            <div className="lds-ripple">
                                <div></div>
                                <div></div>
                            </div>
                        )}
                        {hasSearched &&
                            searchResultCount === 0 &&
                            !isLoading && (
                                <div className="empty-state">
                                    <i className="icon-no-results"></i>
                                    <p>
                                        <b>{props.dictNoResultTitle}</b>
                                    </p>
                                    <p>{props.dictNoResultDescription}</p>
                                </div>
                            )}
                        {searchResultCount > 0 && (
                            <div className="results-topbar">
                                <span className="number-results">
                                    {searchResultCount}{' '}
                                    {searchResultCount > 1
                                        ? props.dictResultsLabel
                                        : props.dictResultLabel}
                                </span>
                            </div>
                        )}
                        {!currentAreaHasResult &&
                            !isLoading &&
                            searchResultCount > 0 && (
                                <div className="empty-state empty-state--spacing">
                                    <i className="icon-no-results"></i>
                                    <p>
                                        {props.dictNoResultForCurrentAreaTitle}
                                    </p>
                                </div>
                            )}
                        <InfiniteScroll
                            className="tiles"
                            hasMore={hasMoreResult}
                            loader={
                                <div key={'loader'} className="lds-ripple">
                                    <div></div>
                                    <div></div>
                                </div>
                            }
                            loadMore={handleLoadMore}
                        >
                            {searchResultItems.map(
                                (pageLink, index) =>
                                    pageLink && (
                                        <ContentLink
                                            key={index}
                                            breadCrumb={pageLink.breadCrumb}
                                            description={pageLink.description}
                                            title={pageLink.name}
                                            url={pageLink.url}
                                        />
                                    )
                            )}
                        </InfiniteScroll>
                    </div>
                </div>
            </div>
        </>
    );
};

WidgetHelper.register(ContentSearchWidget, 'content-search-widget');
